<script>
  import { onMount } from 'svelte';

  import { X, Info, TriangleAlert, Trash2 } from 'lucide-svelte';

  import { confirmStore } from './confirm';

  const themes = {
    info: {
      header: '#dbeaff',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      button: 'bg-blue-600 hover:bg-blue-700',
      icon: Info
    },

    warning: {
      header: '#fff4d6',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      button: 'bg-amber-500 hover:bg-amber-600',
      icon: TriangleAlert
    },

    delete: {
      header: '#ffe5e5',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      button: 'bg-red-600 hover:bg-red-700',
      icon: Trash2
    }
  };

  $: theme = themes[$confirmStore?.type || 'info'];
  $: Icon = theme?.icon;

  function close(result = false) {
    if (!$confirmStore) return;

    $confirmStore.resolve(result);
    confirmStore.set(null);
  }

  function backdropClick(event) {
    if (event.target === event.currentTarget) {
      close(false);
    }
  }

  function handleKey(event) {
    if (event.key === 'Escape') {
      close(false);
    }
  }

  onMount(() => {
    window.addEventListener('keydown', handleKey);

    return () => {
      window.removeEventListener('keydown', handleKey);
    };
  });
</script>

{#if $confirmStore}
  <div
    class="fixed inset-0 z-[9999]
    bg-black/20 backdrop-blur-[1px]
    flex items-center justify-center p-4"
    on:click={backdropClick}>
    <div
      class="w-[420px] max-w-full
      bg-white
      border border-gray-300
      rounded-lg
      shadow-xl
      overflow-hidden
      animate-in">
      <!-- HEADER -->

      <div
        class="h-10 px-3
        flex items-center justify-between
        border-b border-gray-300"
        style="background:{theme.header}">
        <div class="flex items-center gap-2">
          <div
            class={`w-6 h-6 rounded-full
            flex items-center justify-center
            ${theme.iconBg}`}>
            <svelte:component this={Icon} size={14} class={theme.iconColor} />
          </div>

          <span class="text-sm font-semibold text-slate-800">
            {$confirmStore.title}
          </span>
        </div>

        <button
          class="w-6 h-6 rounded
          hover:bg-black/5
          flex items-center justify-center"
          on:click={() => close(false)}>
          <X size={14} />
        </button>
      </div>

      <!-- BODY -->

      <div class="px-4 py-4">
        <div
          class="text-sm text-slate-700
          leading-6 whitespace-pre-wrap">
          {$confirmStore.message}
        </div>
      </div>

      <!-- FOOTER -->

      <div
        class="px-4 py-3
        border-t border-gray-300
        bg-gray-50
        flex justify-end gap-2">
        <button
          class="h-9 px-4
          text-sm
          border border-gray-300
          rounded-md
          bg-white
          hover:bg-gray-50"
          on:click={() => close(false)}>
          {$confirmStore.cancelText}
        </button>

        <button
          class={`h-9 px-4
          text-sm
          rounded-md
          text-white
          ${theme.button}`}
          on:click={() => close(true)}>
          {$confirmStore.confirmText}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .animate-in {
    animation: dialog-show 0.15s ease-out;
  }

  @keyframes dialog-show {
    from {
      opacity: 0;
      transform: translateY(-6px) scale(0.98);
    }

    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
</style>
